/**
 * Branch Drift dashboard widget — Item 1 of "Stop Sunday from happening again".
 *
 * Compact card matching the AdminDashboard KpiTile aesthetic. Polls the
 * /api/admin/cicd/branch-drift endpoint every 60s; navigates to
 * /admin/cicd/drift on click.
 */
import React, { useEffect, useState } from 'react';
import { GitBranch, AlertTriangle, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { adminFetch } from '../../utils/adminApi';

interface DriftResponse {
  drift: { staging_ahead: number; main_ahead: number };
  staging_only_commits: Array<{ age_minutes: number }>;
  main_only_commits: Array<{ age_minutes: number }>;
  alert_level: 'ok' | 'warn' | 'critical';
}

function formatAge(min: number): string {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export const BranchDriftWidget: React.FC = () => {
  const navigate = useNavigate();
  // Plain useEffect + fetch — avoids dependency on react-query's
  // QueryClientProvider which isn't wired up on the admin Dashboard
  // route. Polls every 60s on its own. (Same pattern as the BranchDrift
  // detail page.)
  const [data, setData] = useState<DriftResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await adminFetch<DriftResponse>('/api/admin/cicd/branch-drift');
        if (!cancelled) {
          setData(res);
          setIsLoading(false);
        }
      } catch (_err) {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const totalDrift = (data?.drift.staging_ahead ?? 0) + (data?.drift.main_ahead ?? 0);
  const allCommits = [...(data?.staging_only_commits ?? []), ...(data?.main_only_commits ?? [])];
  const oldestAge = allCommits.length > 0 ? Math.max(...allCommits.map(c => c.age_minutes)) : 0;
  // When there's no observable drift (0/0), always treat as healthy/green —
  // alert_level from backend can be "critical" when the GitHub API call
  // errors with no data to compare against, which is a different signal
  // (drift-check unavailable) and shouldn't render as a drift alarm.
  const level: 'ok' | 'warn' | 'critical' =
    totalDrift === 0 ? 'ok' : (data?.alert_level ?? 'ok');

  const tone =
    level === 'critical' ? { fg: 'var(--status-error-text)', body: `${totalDrift} commits stuck` } :
    level === 'warn' ? { fg: 'var(--status-warning-text)', body: `${totalDrift} commits stuck` } :
    { fg: 'var(--status-success-text)', body: 'main ↔ staging in sync' };

  return (
    <button
      onClick={() => navigate('/admin/cicd/drift')}
      style={{
        textAlign: 'left',
        background: 'var(--surface)',
        borderRadius: 14,
        boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)',
        padding: 16,
        cursor: 'pointer',
        border: 'none',
        width: '100%',
        transition: 'transform 100ms ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Branch Drift
        </span>
        <GitBranch style={{ width: 14, height: 14, color: 'var(--text-secondary)' }} />
      </div>
      {isLoading && !data ? (
        <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>Loading…</div>
      ) : (
        <>
          <div style={{ fontSize: 18, fontWeight: 700, color: tone.fg, display: 'flex', alignItems: 'center', gap: 6 }}>
            {level === 'ok'
              ? <Check style={{ width: 16, height: 16 }} />
              : <AlertTriangle style={{ width: 16, height: 16 }} />}
            <span>{tone.body}</span>
          </div>
          {totalDrift > 0 && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
              oldest: {formatAge(oldestAge)}
            </div>
          )}
        </>
      )}
    </button>
  );
};

export default BranchDriftWidget;
